import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { DashboardSidebar } from './DashboardSidebar';
import { DashboardHeader } from './DashboardHeader';
import { CommandCenter } from './CommandCenter';
import { ModalProvider } from '../../src/ui/modal/ModalProvider';
import { ModalRoot } from '../../src/ui/modal/ModalRoot';
import { OnboardingFlowUniversal } from '../como/OnboardingFlowUniversal';
import { TermsUpdateModal } from '../TermsUpdateModal';
import { useObservabilityTelemetry } from '../../hooks/useObservabilityTelemetry';
import { isMockDataAllowed } from '../../services/backendMode';
import { TeamChatBootstrap } from '../../modules/team-chat/store/TeamChatBootstrap';
import { IntegrationsBootstrap } from '../../modules/integrations/IntegrationsBootstrap';

// Enhanced loading component
const DashboardLoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-cyber-cyan/20 border-t-cyber-cyan rounded-full animate-spin" />
      <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-cyber-blue rounded-full animate-spin" style={{ animationDelay: '150ms', animationDirection: 'reverse' }} />
      <div className="absolute inset-2 w-12 h-12 border-4 border-cyber-purple/30 border-b-cyber-purple rounded-full animate-spin" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

export function DashboardRoot() {
  useObservabilityTelemetry();
  const mockMode = isMockDataAllowed();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Restore sidebar state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Global search keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open command center
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Convert URL path to page ID for sidebar
  const getPageIdFromPath = (pathname: string): string => {
    // Remove leading slash
    const path = pathname.replace(/^\//, '');
    
    // Handle empty path (dashboard root) - default to super-dashboard
    if (!path) return 'overview/super-dashboard';
    
    return path;
  };

  const currentPageId = getPageIdFromPath(location.pathname);

  const handleNavigate = (pageId: string) => {
    // Convert pageId to route path
    navigate(`/${pageId}`);
    setIsMobileMenuOpen(false);
  };

  return (
    <ModalProvider>
      <IntegrationsBootstrap>
      <TeamChatBootstrap>
      <div className="cm-dashboard-shell h-[100dvh] min-h-[100dvh] flex overflow-hidden relative isolate">
        {/* Enhanced Futuristic Background */}
        <div className="fixed inset-0 -z-10">
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
          
          {/* Cyber grid pattern */}
          <div className="absolute inset-0 opacity-[0.02]" 
               style={{
                 backgroundImage: `
                   linear-gradient(rgba(0, 209, 255, 0.1) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(0, 209, 255, 0.1) 1px, transparent 1px)
                 `,
                 backgroundSize: '50px 50px'
               }} />
          
          {/* Animated gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyber-cyan/10 to-cyber-blue/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-cyber-purple/10 to-cyber-magenta/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-gradient-to-r from-cyber-green/10 to-cyber-cyan/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }} />
          
          {/* Radial overlays */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(0,209,255,0.05),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(169,107,255,0.05),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,102,255,0.03),transparent_70%)]" />
        </div>

        {/* Sidebar */}
        <DashboardSidebar
          currentPage={currentPageId}
          onNavigate={handleNavigate}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isMobileMenuOpen={isMobileMenuOpen}
          onToggleMobileMenu={() => setIsMobileMenuOpen((prev) => !prev)}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0 relative w-full">
          {/* Header */}
          <DashboardHeader 
            currentPage={currentPageId}
            onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onToggleMobileMenu={() => setIsMobileMenuOpen((prev) => !prev)}
            onOpenSearch={() => setIsSearchOpen(true)}
          />

          {mockMode && (
            <div className="px-4 md:px-6 py-2 border-b border-yellow-400/30 bg-yellow-400/10 text-yellow-200 text-xs">
              Sample data mode enabled (`VITE_ALLOW_MOCK_DATA=true`). Visual testing data may appear in some tabs.
            </div>
          )}

          {/* Global Search Modal */}
          <CommandCenter isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

          {/* Main Content with Page Transitions */}
          <main
            data-dashboard-scroll="true"
            data-scroll-zone="main"
            className="cm-main-scroll-zone flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-y-contain relative touch-pan-y"
          >
            <div className="h-full min-h-0 relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ 
                    duration: 0.4, 
                    ease: [0.25, 0.46, 0.45, 0.94] 
                  }}
                  className="h-full min-h-0 cm-content-max"
                >
                  <React.Suspense fallback={<DashboardLoadingSpinner />}>
                    <Outlet />
                  </React.Suspense>
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
        
        {/* Ambient glow effects */}
        <div className="fixed top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyber-cyan/30 to-transparent" />
        <div className="fixed bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyber-purple/30 to-transparent" />
        
        {/* COMO AI and Onboarding - Universal for all users */}
        <OnboardingFlowUniversal />
        <TermsUpdateModal />
      </div>
      </TeamChatBootstrap>
      </IntegrationsBootstrap>
      <ModalRoot />
    </ModalProvider>
  );
}